import axios, { AxiosError, AxiosResponse, AxiosStatic } from 'axios';
import { all, call, put, takeLatest } from '@redux-saga/core/effects';

import { SNACK_CRITICAL, SNACK_SUCCESS } from '@neslotech/utils';

import {
  getAllDownloadTombstonesRequest,
  getAllTombstonesRequest,
  getUpdateTombstoneV3Request,
  getCreateTombstoneV3Request,
  RequestOptions
} from '../tools/api';

import { Tombstone } from '../interfaces/client/tombstone.interface';

import { LoadTombstonesAction, UpdateTombstoneAction } from '../actions/tombstone/tombstone.types';
import { TombstoneActions } from '../actions/tombstone/tombstone.actions';
import { SystemActions } from '../actions/system/system.actions';
import { LoadTombstoneAction } from '../actions/client/tombstone/tombstone.types';

export function* performLoadTombstones({ onComplete }: LoadTombstonesAction) {
  try {
    // get endpoint and http request options
    const [endpoint, requestOptions]: RequestOptions = getAllTombstonesRequest();

    // make the request, no need to check the response
    const { data } = yield call<AxiosStatic>(axios, endpoint, requestOptions);

    yield put({ type: TombstoneActions.SET_TOMBSTONES, tombstones: data });
  } catch (error) {
    if (!(error instanceof AxiosError)) {
      throw error;
    }

    const message = error.response.data.message;
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForLoadTombstonesRequest() {
  yield takeLatest(TombstoneActions.LOAD_TOMBSTONES, performLoadTombstones);
}

export function* performDownloadTombstones({ onComplete }: LoadTombstoneAction) {
  try {
    // get endpoint and http request options
    const [endpoint, requestOptions]: RequestOptions =
      getAllDownloadTombstonesRequest() as RequestOptions;

    const { data, headers } = yield call(axios, endpoint, requestOptions);

    // Get the Content-Disposition header value from the response
    const contentDisposition = headers['content-disposition'];

    // Use a regular expression to extract the file name from the header value
    const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = regex.exec(contentDisposition);
    const filename = matches && matches[1] ? matches[1].replace(/['"]/g, '') : 'unknown';

    const csvBlob = new Blob([data], { type: 'text/csv' });
    const anchor = document.createElement('a');
    anchor.download = filename;
    anchor.href = window.URL.createObjectURL(csvBlob);
    anchor.target = '_blank';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } catch (error) {
    if (!(error instanceof AxiosError)) {
      throw error;
    }

    const message = error.response.data.message;
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForDownloadTombstonesRequest() {
  yield takeLatest(TombstoneActions.DOWNLOAD_TOMBSTONES, performDownloadTombstones);
}

/**
 * v3 in-place update.  PUT /tombstones/:id then patch the saved record into
 * the existing tombstones[] so list pages re-render without a full refetch.
 */
export function* performUpdateTombstone({ id, payload, onComplete }: UpdateTombstoneAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getUpdateTombstoneV3Request(id, payload);
    const response: AxiosResponse = yield call<AxiosStatic>(axios, endpoint, requestOptions);

    yield put({ type: TombstoneActions.SET_TOMBSTONE, tombstone: response.data });
    yield put(SystemActions.addNotice('Tombstone updated.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message =
      error.response?.data?.message ||
      (typeof error.response?.data === 'object'
        ? Object.entries(error.response.data)
            .map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
        : 'Unable to update tombstone.');
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForUpdateTombstoneRequest() {
  yield takeLatest(TombstoneActions.UPDATE_TOMBSTONE, performUpdateTombstone);
}

interface CreateTombstoneAction {
  type: string;
  payload: Partial<Tombstone>;
  onComplete: (created?: Tombstone) => void;
}

/**
 * v3 — POST /tombstones with the full payload (client_id in the body).
 * Refresh the tombstones list so any page already showing tombstones
 * (Tombstones list, Client detail Tombstones tab, Dashboard track-record
 * counts) reflects the new row.  Forwards the created tombstone to
 * onComplete so the caller can navigate to its detail page.
 */
export function* performCreateTombstone({ payload, onComplete }: CreateTombstoneAction) {
  let created: Tombstone | undefined;
  try {
    const [endpoint, requestOptions]: RequestOptions = getCreateTombstoneV3Request(payload);
    const response: AxiosResponse<Tombstone> = yield call<AxiosStatic>(
      axios,
      endpoint,
      requestOptions
    );
    created = response.data;

    // Refresh the global tombstones list + push the new record to SET_TOMBSTONE
    // so detail pages / dashboards already mounted pick it up immediately.
    const [listEndpoint, listOptions]: RequestOptions = getAllTombstonesRequest();
    const list = yield call<AxiosStatic>(axios, listEndpoint, listOptions);
    yield put({ type: TombstoneActions.SET_TOMBSTONES, tombstones: list.data });
    yield put({ type: TombstoneActions.SET_TOMBSTONE, tombstone: created });

    yield put(SystemActions.addNotice('Tombstone created.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message =
      error.response?.data?.message ||
      (typeof error.response?.data === 'object'
        ? Object.entries(error.response.data)
            .map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
        : 'Unable to create tombstone.');
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete, created);
  }
}

export function* watchForCreateTombstoneRequest() {
  yield takeLatest(TombstoneActions.CREATE_TOMBSTONE, performCreateTombstone);
}

export default function* tombstoneSaga() {
  yield all([
    watchForLoadTombstonesRequest(),
    watchForDownloadTombstonesRequest(),
    watchForUpdateTombstoneRequest(),
    watchForCreateTombstoneRequest()
  ]);
}
